import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListRequestOfProgressionGateTypeFilter,
  ListResponseOfProgressionGateType,
  ListResponseOfSkuFormat,
  OrganizationDetails,
  SkuProgressionGate,
  SkuProgressionGateRequest,
} from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";

interface ProgressionGateFormProps {
  organization?: OrganizationDetails;
  sku?: SkuFormProps["sku"];
  progressionGate?: SkuProgressionGate | null;
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
}

interface ProgressionGateFormValues {
  id?: string | null;
  skuFormatId: string;
  progressionGateTypeId: string;
  dialogueTitle?: string;
  promptMessage: string;
  labelText: string;
  value: string;
  errorMessage: string;
}

const ProgressionGateForm = ({
  organization,
  sku,
  progressionGate,
  onClose,
  onCreate,
  onUpdate,
}: ProgressionGateFormProps) => {
  const { formatMessage: t } = useIntl();

  const {
    organizationService,
    skuService,
    progressionGateService,
  } = useGlobalContext();
  const queryClient = useQueryClient();

  const init = useMemo<ProgressionGateFormValues>(
    () => ({
      id: progressionGate?.id,
      skuFormatId: progressionGate?.skuFormatId ?? sku?.skuFormatId ?? "",
      progressionGateTypeId: progressionGate?.progressionGateTypeId ?? "",
      promptMessage: progressionGate?.prompt ?? "",
      labelText: progressionGate?.label ?? "",
      value: progressionGate?.value ?? "",
      errorMessage: progressionGate?.errorMessage ?? "",
      dialogueTitle: progressionGate?.dialogueTitle,
    }),
    [progressionGate, sku]
  );

  const {
    data: progressionGateTypes,
  } = useStrictQuery<ListResponseOfProgressionGateType>(
    ["progressionGateTypes"],
    () =>
      progressionGateService.getProgressionGateTypes(
        ListRequestOfProgressionGateTypeFilter.fromJS({
          sortField: "sequence",
          parameters: {
            includeNone: false,
          },
        })
      )
  );

  const { data: skuFormats } = useStrictQuery<ListResponseOfSkuFormat>(
    ["sku-formats"],
    () =>
      skuService.getSkuFormats(
        ListRequestOfBaseFilter.fromJS({
          sortField: "name",
        })
      )
  );

  const onSubmit = async (values: ProgressionGateFormValues) => {
    try {
      const formData = SkuProgressionGateRequest.fromJS({
        id: values.id,
        progressionGateTypeId: values.progressionGateTypeId,
        skuId: sku?.id,
        organizationId: organization?.id ?? sku?.ownerOrganizationId,
        skuFormatId: values.skuFormatId ?? sku?.skuFormatId,
        promptMessage: values.promptMessage,
        labelText: values.labelText,
        value: values.value,
        dialogueTitle: values.dialogueTitle,
        errorMessage: values.errorMessage,
      });

      if (organization) {
        await organizationService.saveDefaultProgressionGate(formData);
        queryClient.invalidateQueries([
          "defaultProgressionGates",
          organization.id,
        ]);
      } else {
        await skuService.saveSkuProgressionGate(formData);
        queryClient.invalidateQueries(["sku-progression-gates", sku!.id]);
      }

      if (!values.id) {
        onCreate();
      } else {
        onUpdate();
      }
    } catch (e) {
      return { [FORM_ERROR]: translateError(e, t) };
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth disableEnforceFocus>
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle>
              {progressionGate ? (
                <T
                  id={
                    organization
                      ? "editDefaultProgressionGate"
                      : "editCourseProgressionGate"
                  }
                />
              ) : (
                <T
                  id={
                    organization
                      ? "addDefaultProgressionGate"
                      : "courseProgressionGateModalTitle"
                  }
                />
              )}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={4}>
                {!sku && (
                  <Grid item xs={12}>
                    <Field name="skuFormatId" required>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <TextField
                            {...input}
                            select
                            variant="standard"
                            required
                            disabled={!!sku}
                            label={t({ id: "skuFormatLabel" })}
                            inputProps={{
                              maxLength: 250,
                            }}
                            InputLabelProps={{ required: true }}
                            error={meta.error && meta.submitFailed}
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                          >
                            {(skuFormats?.data ?? []).map((format) => (
                              <MenuItem key={format.id} value={format.id}>
                                {format.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Field name="progressionGateTypeId" required>
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <TextField
                          {...input}
                          select
                          variant="standard"
                          required
                          label={t({ id: "gateTypeLabel" })}
                          inputProps={{
                            maxLength: 250,
                          }}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                        >
                          {(progressionGateTypes?.data ?? []).map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="dialogueTitle" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        variant="standard"
                        fullWidth
                        required
                        label={t({ id: "dialogueTitleLabel" })}
                        inputProps={{
                          maxLength: 250,
                        }}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="promptMessage" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        variant="standard"
                        fullWidth
                        required
                        label={t({ id: "promptLabel" })}
                        inputProps={{
                          maxLength: 250,
                        }}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="labelText" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        variant="standard"
                        fullWidth
                        required
                        label={t({ id: "placeholderLabel" })}
                        inputProps={{
                          maxLength: 60,
                        }}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="value" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        variant="standard"
                        fullWidth
                        required
                        label={t({
                          id: "courseProgressionRequiredResponseLabel",
                        })}
                        inputProps={{
                          maxLength: 250,
                        }}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="errorMessage" required>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        variant="standard"
                        fullWidth
                        required
                        label={t({ id: "errorMessageField" })}
                        inputProps={{
                          maxLength: 250,
                        }}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    )}
                  </Field>
                </Grid>

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {submitting && <CircularProgress size={16} />}
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting}>
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default withErrorBoundary(ProgressionGateForm);
